import { rem } from 'polished'
import React from 'react'
import { DATA_TEST_CLASS } from 'shared-constants/build/testIds'
import styled from 'styled-components'
import Icon, { ICON_SIZE } from 'components/Icon'
import { Input, InputArea, InputWrapper } from 'components/Input'
import { Flex } from 'components/Layout'
import { SPACE, COLOR_INTENT } from 'Theme'

const COUNTER_INPUT_WIDTH = rem(40)

const StyledInputWrapper = styled(InputWrapper)`
  padding: 0;
`

const StyledInputArea = styled(InputArea)`
  text-align: center;
  padding: 0 ${SPACE.PX_5};
`

const IconButton = styled.button`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  align-self: center;
`

interface CounterProps extends React.ComponentProps<typeof Input> {
  id: string
  value: number
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
  onIncrement: () => void
  onDecrement: () => void
  isDecrementDisabled?: boolean
  isIncrementDisabled?: boolean
  isDisabled?: boolean
}

export const Counter: React.FC<CounterProps> = ({
  value,
  onChange,
  onIncrement,
  onDecrement,
  isIncrementDisabled,
  isDecrementDisabled,
  isDisabled,
  id,
  ...props
}) => {
  const isIncrementOrInputDisabled = isIncrementDisabled || isDisabled
  const isDecrementOrInputDisabled = isDecrementDisabled || isDisabled

  return (
    <Input {...props} htmlFor={id} aria-label="Counter input">
      {({
        isFocused,
        isWarning,
        isError,
        size,
        borderColor,
        setIsFocused,
        onBlurHandler,
        placeholder,
      }) => (
        <Flex>
          <IconButton
            data-test-id={DATA_TEST_CLASS.COUNTER_DECREMENT_BUTTON}
            data-test-class={DATA_TEST_CLASS.COUNTER_DECREMENT_BUTTON}
            disabled={isDecrementOrInputDisabled}
            onClick={() => {
              if (!isDecrementOrInputDisabled) {
                onDecrement()
              }
            }}
          >
            <Icon.Minus
              size={ICON_SIZE.PX_20}
              color={
                isDecrementOrInputDisabled
                  ? COLOR_INTENT.COUNTER.ICON_DISABLED
                  : COLOR_INTENT.COUNTER.ICON_ENABLED
              }
            />
          </IconButton>
          <Flex
            width={COUNTER_INPUT_WIDTH}
            mx={SPACE.PX_10}
            alignItems="center"
          >
            <StyledInputWrapper
              isFocused={isFocused}
              isWarning={isWarning}
              isError={isError}
              size={size}
              isDisabled={isDisabled}
              hasIcon={Boolean(Icon)}
              borderColor={borderColor}
            >
              <StyledInputArea
                id={id}
                onFocus={() => {
                  setIsFocused(true)
                }}
                onBlur={(event) => {
                  setIsFocused(false)
                  if (onBlurHandler) {
                    onBlurHandler(event)
                  }
                }}
                isWarning={isWarning}
                isError={isError}
                size={size}
                disabled={isDisabled}
                placeholder={placeholder}
                hasIcon={Boolean(Icon)}
                onChange={onChange}
                value={value}
                {...props}
              />
            </StyledInputWrapper>
          </Flex>
          <IconButton
            data-test-class={DATA_TEST_CLASS.COUNTER_INCREMENT_BUTTON}
            data-test-id={DATA_TEST_CLASS.COUNTER_INCREMENT_BUTTON}
            disabled={isIncrementOrInputDisabled}
            onClick={() => {
              if (!isIncrementOrInputDisabled) {
                onIncrement()
              }
            }}
          >
            <Icon.Plus
              size={ICON_SIZE.PX_20}
              color={
                isIncrementOrInputDisabled
                  ? COLOR_INTENT.COUNTER.ICON_DISABLED
                  : COLOR_INTENT.COUNTER.ICON_ENABLED
              }
            />
          </IconButton>
        </Flex>
      )}
    </Input>
  )
}
